import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  Form,
  Card,
  Tabs,
  Input,
  InputNumber,
  Select,
  Switch,
  Upload,
  Image,
  Button,
  message,
  Spin,
  Typography,
  Space,
  Tag,
  Descriptions,
  Table,
  Row,
  Col,
  Progress,
  Popconfirm,
} from 'antd';
import { ArrowLeftOutlined, UploadOutlined, SaveOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import type { UploadRequestOption } from 'rc-upload/lib/interface';
import type { RcFile } from 'antd/es/upload';
import type { ColumnsType } from 'antd/es/table';
import api from '../services/api';
import ImageUpload, { ALLOWED_IMAGE_TYPES, MAX_IMAGE_SIZE, describeUploadError } from '../components/ImageUpload';
import AvailabilityEditor, { AvailabilityPolicyValue, validateAvailabilityPolicy } from '../components/AvailabilityEditor';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

// ─── Типы ────────────────────────────────────────────────────────────────────

interface Category {
  id: number;
  name: string;
}

interface Sticker {
  id: number;
  name: string;
  emoji?: string;
}

interface Variant {
  id: number;
  name: string;
  price: number;
}

interface AddonCategory {
  id: number;
  name: string;
}

interface Branch {
  id: number;
  name: string;
}

interface BranchArticle {
  branch_id: number;
  branch_name: string;
  crm_article: string;
  is_in_stop_list: boolean;
}

interface Product {
  id: number;
  name: string;
  slug: string;
  short_description: string;
  description: string;
  category_id: number | null;
  price: number;
  old_price: number | null;
  crm_article: string;
  branch_articles: BranchArticle[];
  sort_order: number;
  weight: string;
  portion_value: number | null;
  portion_unit: string | null;
  pieces_count: number | null;
  calories: string | null;
  proteins: string | null;
  fats: string | null;
  carbs: string | null;
  is_published: boolean;
  is_in_stop_list: boolean;
  is_recommended: boolean;
  stock_quantity: number | null;
  image_url: string;
  gallery: string[];
  stickers: Sticker[];
  tags: { id: number; name: string }[];
  variants: Variant[];
  addon_categories: AddonCategory[];
  availability: AvailabilityPolicyValue | null;
  created_at: string;
  updated_at: string;
}

// ─── Чтение сообщения об ошибке сохранения ───────────────────────────────────
// Backend может вернуть detail-строку (409/400/503) или FastAPI 422 validation
// errors (список объектов с loc/msg) — показываем читаемый текст вместо общей
// "ошибки сети", чтобы было понятно, что реально не так.
export function describeSaveError(err: any): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((e: any) => {
        const field = Array.isArray(e?.loc) ? e.loc[e.loc.length - 1] : '';
        return field ? `${field}: ${e.msg}` : e.msg;
      })
      .join('; ');
  }
  if (err?.response?.status === 503) return 'Сервер временно занят, повторите попытку';
  if (!err?.response) return 'Нет соединения с сервером, попробуйте снова';
  return 'Ошибка сохранения товара';
}

// ─── Основной компонент ──────────────────────────────────────────────────────

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  // Ref — защита от второго клика до того, как React перерисует состояние
  // saving: между двумя быстрыми кликами await form.validateFields() ещё не
  // вернул результат, и stale `saving`-state из первого рендера пропускает
  // оба запроса. Ref читается синхронно, до первого await.
  const savingRef = useRef(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [allStickers, setAllStickers] = useState<Sticker[]>([]);
  const [allAddonCategories, setAllAddonCategories] = useState<AddonCategory[]>([]);
  const [allBranches, setAllBranches] = useState<Branch[]>([]);
  const [branchArticles, setBranchArticles] = useState<Record<number, string>>({});
  const [branchStopList, setBranchStopList] = useState<Record<number, boolean>>({});

  // ─── Загрузка данных ────────────────────────────────────────────────────

  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        if (isNew) {
          // Режим создания — загружаем только справочники
          const [categoriesRes, stickersRes, addonCatRes, branchesRes] = await Promise.all([
            api.get<Category[]>('/admin/categories', { params: { include_hidden: true } }),
            api.get<Sticker[]>('/admin/stickers'),
            api.get('/admin/modifier-groups'),
            api.get<Branch[]>('/admin/branches'),
          ]);
          setCategories(categoriesRes.data);
          setAllStickers(stickersRes.data);
          setAllAddonCategories(addonCatRes.data);
          setAllBranches(branchesRes.data);
          form.setFieldsValue({ is_published: false, sort_order: 0 });
        } else {
          const [productRes, categoriesRes, stickersRes, addonCatRes, branchesRes] = await Promise.all([
            api.get<Product>(`/admin/products/${id}`, { params: { include_hidden: true } }),
            api.get<Category[]>('/admin/categories', { params: { include_hidden: true } }),
            api.get<Sticker[]>('/admin/stickers'),
            api.get('/admin/modifier-groups'),
            api.get<Branch[]>('/admin/branches'),
          ]);

          const prod = productRes.data;
          setProduct(prod);
          setCategories(categoriesRes.data);
          setAllStickers(stickersRes.data);
          setAllAddonCategories(addonCatRes.data);
          setAllBranches(branchesRes.data);

          // Инициализируем per-branch CRM-артикулы из ответа API
          const articlesMap: Record<number, string> = {};
          const stopMap: Record<number, boolean> = {};
          (prod.branch_articles || []).forEach((ba: BranchArticle) => {
            if (ba.crm_article) articlesMap[ba.branch_id] = ba.crm_article;
            if (ba.is_in_stop_list) stopMap[ba.branch_id] = true;
          });
          setBranchArticles(articlesMap);
          setBranchStopList(stopMap);

          // Заполняем форму данными товара
          form.setFieldsValue({
            ...prod,
            sticker_ids: prod.stickers?.map((s) => s.id) ?? [],
            tag_ids: prod.tags?.map((t) => t.id) ?? [],
            addon_category_ids: prod.addon_categories?.map((ac) => ac.id) ?? [],
          });
        }
      } catch (err) {
        if (isNew) {
          message.warning('Не удалось загрузить справочники. Попробуйте обновить страницу.');
        } else {
          message.error('Ошибка загрузки товара');
        }
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, form]);

  // ─── Сохранение ─────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (savingRef.current) return; // защита от повторного клика пока первый save в полёте
    if (mainImageUploadingRef.current || galleryUploadingRef.current) {
      message.warning('Дождитесь завершения загрузки изображения');
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      let values: any;
      try {
        values = await form.validateFields();
      } catch {
        // ошибки валидации уже отображены на полях формы
        return;
      }

      // Повторная проверка: загрузка фото могла начаться, пока шёл await
      // form.validateFields() выше — без этого второй проверки можно уйти
      // в сеть с полём image_url/gallery в промежуточном состоянии.
      if (mainImageUploadingRef.current || galleryUploadingRef.current) {
        message.warning('Дождитесь завершения загрузки изображения');
        return;
      }

      const data = {
        name: values.name,
        slug: values.slug,
        short_description: values.short_description,
        description: values.description,
        category_id: values.category_id,
        price: values.price,
        old_price: values.old_price,
        crm_article: values.crm_article,
        sort_order: values.sort_order,
        weight: values.weight,
        portion_value: values.portion_value ?? null,
        portion_unit: values.portion_unit || null,
        pieces_count: values.pieces_count ?? null,
        calories: values.calories == null ? null : String(values.calories),
        proteins: values.proteins == null ? null : String(values.proteins),
        fats: values.fats == null ? null : String(values.fats),
        carbs: values.carbs == null ? null : String(values.carbs),
        is_published: values.is_published,
        is_in_stop_list: values.is_in_stop_list,
        is_recommended: !!values.is_recommended,
        stock_quantity: values.stock_quantity ?? null,
        image_url: values.image_url,
        gallery: values.gallery,
        sticker_ids: values.sticker_ids ?? [],
        tag_ids: values.tag_ids ?? [],
        addon_category_ids: values.addon_category_ids ?? [],
        // availability всегда отправляется явно (объект или null) — PUT/POST
        // продукта использует exclude_unset на backend, но поле присутствует
        // в каждом запросе с формы, поэтому несохранённые изменения не теряются,
        // а неизменённое значение просто возвращается как есть (round-trip).
        availability: values.availability ?? null,
        // Per-branch CRM-артикулы и стоп-лист: отправляем массив
        branch_articles: allBranches
          .filter((branch) => branchArticles[branch.id] || branchStopList[branch.id])
          .map((branch) => ({
            branch_id: branch.id,
            crm_article: (branchArticles[branch.id] || '').trim() || null,
            is_in_stop_list: !!branchStopList[branch.id],
          })),
      };

      if (isNew) {
        const res = await api.post('/admin/products', data);
        message.success('Товар успешно создан');
        navigate(`/admin/products/${res.data.id}`, { replace: true });
      } else {
        await api.put(`/admin/products/${id}`, data);
        message.success('Товар успешно сохранён');
      }
    } catch (err: any) {
      // Введённые данные остаются в форме — не сбрасываем её при ошибке.
      message.error(describeSaveError(err));
      console.error(err);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // ─── Главное изображение: смена значения из ImageUpload ────────────────

  const handleMainImageChange = (url: string) => {
    form.setFieldValue('image_url', url);
    setProduct((prev) => (prev ? { ...prev, image_url: url } : ({ image_url: url } as Product)));
  };

  // ─── Главное изображение: блокирует Save и галерею, пока идёт загрузка ──

  const [mainImageUploading, setMainImageUploading] = useState(false);
  // Синхронный аналог mainImageUploading — handleSave читает его ДО того,
  // как React успеет перерисовать state (см. savingRef).
  const mainImageUploadingRef = useRef(false);
  const handleMainImageUploadingChange = (value: boolean) => {
    mainImageUploadingRef.current = value;
    setMainImageUploading(value);
  };

  // ─── Галерея: загрузка нового изображения (прогресс + валидация) ───────

  const [galleryUploading, setGalleryUploading] = useState(false);
  const galleryUploadingRef = useRef(false);
  const [galleryProgress, setGalleryProgress] = useState(0);
  const [galleryProgressKnown, setGalleryProgressKnown] = useState(false);
  const [galleryTransferComplete, setGalleryTransferComplete] = useState(false);

  const beforeGalleryUpload = (file: RcFile): boolean => {
    if (galleryUploadingRef.current) {
      message.warning('Подождите, пока завершится текущая загрузка');
      return false;
    }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      message.error('Допустимы только JPG, PNG и WebP изображения');
      return false;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      message.error('Размер файла не должен превышать 5 МБ');
      return false;
    }
    return true;
  };

  const handleGalleryUpload = async (options: UploadRequestOption) => {
    const { file, onSuccess, onError } = options;

    // Синхронный ref-лок — защита от гонки, когда antd Upload вызывает
    // customRequest для нескольких одновременно выбранных/перетащенных
    // файлов до того, как успеет примениться state `galleryUploading`.
    if (galleryUploadingRef.current) {
      onError?.(new Error('Уже идёт другая загрузка'));
      return;
    }
    galleryUploadingRef.current = true;

    const formData = new FormData();
    formData.append('file', file as File);

    setGalleryUploading(true);
    setGalleryProgress(0);
    setGalleryProgressKnown(false);
    setGalleryTransferComplete(false);

    try {
      const res = await api.post<{ url: string }>('/admin/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          // Без известного total прогресс неизвестен — не подменяем его
          // ложным "почти готово" значением 99%.
          if (!e.total) {
            setGalleryProgressKnown(false);
            return;
          }
          const pct = Math.round((e.loaded * 100) / e.total);
          setGalleryProgressKnown(true);
          setGalleryProgress(Math.min(pct, 99));
          // "Обработка" — только когда байты реально все дошли, не когда
          // округлённый процент случайно достиг 99 при неполной передаче.
          setGalleryTransferComplete(e.loaded >= e.total);
        },
      });
      const url: unknown = res.data?.url;
      if (typeof url !== 'string' || !url) {
        throw new Error('Сервер не вернул ссылку на изображение');
      }
      setGalleryProgress(100);
      setGalleryProgressKnown(true);
      const currentGallery: string[] = form.getFieldValue('gallery') ?? [];
      const updatedGallery = [...currentGallery, url];
      form.setFieldValue('gallery', updatedGallery);
      setProduct((prev) => prev ? { ...prev, gallery: updatedGallery } : { gallery: updatedGallery } as Product);
      message.success('Изображение добавлено в галерею');
      onSuccess?.(res.data);
    } catch (err: any) {
      message.error(describeUploadError(err));
      onError?.(err);
    } finally {
      galleryUploadingRef.current = false;
      setGalleryUploading(false);
      setGalleryTransferComplete(false);
    }
  };

  // ─── Галерея: удаление по индексу (не по URL — важно при дублях) ───────

  const handleGalleryRemove = (index: number) => {
    // Защита от удаления карточки, пока идёт сохранение/загрузка фото —
    // кнопка и так задизейблена, но ref-проверка защищает от гонки клика
    // до перерисовки disabled-атрибута.
    if (savingRef.current || mainImageUploadingRef.current || galleryUploadingRef.current) return;
    const currentGallery: string[] = form.getFieldValue('gallery') ?? [];
    const updatedGallery = currentGallery.filter((_, i) => i !== index);
    form.setFieldValue('gallery', updatedGallery);
    setProduct((prev) => prev ? { ...prev, gallery: updatedGallery } : { gallery: updatedGallery } as Product);
  };

  // ─── Колонки таблицы вариантов ──────────────────────────────────────────

  const variantColumns: ColumnsType<Variant> = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Цена',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => `${price} ₽`,
    },
  ];

  // ─── Форматирование даты ────────────────────────────────────────────────

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('ru-RU');
  };

  // ─── Товар не найден (только после завершения загрузки) ────────────────

  if (!loading && !product && !isNew) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Text type="secondary">Товар не найден</Text>
      </div>
    );
  }

  // ─── Рендер ─────────────────────────────────────────────────────────────
  // ВАЖНО: <Form> монтируется ВСЕГДА (спиннер — наложение поверх, а не
  // условный unmount). Если убирать Form из дерева на время загрузки,
  // form.setFieldsValue() в fetchData() вызывается для ещё не
  // смонтированных Field-компонентов — приводит к тому, что значения не
  // применяются к ранее не существовавшим Field (и к зависанию тестов,
  // которые ждут появления префилженного значения). Монтируя Form сразу,
  // Field-компоненты подписываются на обновления стора ДО того как придут
  // данные, и setFieldsValue корректно их уведомляет.

  return (
    <Spin spinning={loading} size="large" tip="Загрузка товара..." wrapperClassName="product-detail-spin-wrapper">
    <div style={{ padding: 24 }}>
      {/* Заголовок */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/products')}>
            Назад к списку
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            {isNew ? 'Новый товар' : product?.name ?? ''}
          </Title>
          {!isNew && product && <Tag color="default">ID: {product.id}</Tag>}
        </Space>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          disabled={saving || mainImageUploading || galleryUploading}
          onClick={handleSave}
          style={{ backgroundColor: '#FF6B35', borderColor: '#FF6B35' }}
        >
          {isNew ? 'Создать' : 'Сохранить'}
        </Button>
      </div>

      <Row gutter={24}>
        {/* Основной контент с табами */}
        <Col xs={24} lg={18}>
          <Form form={form} layout="vertical">
            {/* Скрытые поля для image_url и gallery */}
            <Form.Item name="image_url" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="gallery" hidden>
              <Input />
            </Form.Item>

            <Tabs defaultActiveKey="1" type="card">
              {/* ─── Вкладка 1: Основная информация ────────────────────── */}
              <TabPane tab="Основная информация" key="1">
                <Card bordered={false}>
                  <Row gutter={24}>
                    <Col xs={24} lg={16}>
                      <Form.Item
                        name="name"
                        label="Название"
                        rules={[{ required: true, message: 'Введите название товара' }]}
                      >
                        <Input placeholder="Введите название" />
                      </Form.Item>

                      <Form.Item name="slug" label="URL-адрес">
                        <Input placeholder="slug-tovara" />
                      </Form.Item>

                      <Form.Item name="short_description" label="Короткое описание">
                        <Input placeholder="Краткое описание для карточки" />
                      </Form.Item>

                      <Form.Item name="description" label="Описание">
                        <TextArea rows={4} placeholder="Полное описание товара" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item name="category_id" label="Категория"
                        rules={[{ required: true, message: 'Выберите категорию' }]}
                      >
                        <Select
                          placeholder="Выберите категорию"
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          options={categories.map((c) => ({ value: c.id, label: c.name }))}
                        />
                      </Form.Item>

                      <Form.Item name="price" label="Цена"
                        rules={[{ required: true, message: 'Укажите цену' }]}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          step={1}
                          addonAfter="₽"
                          placeholder="0"
                        />
                      </Form.Item>

                      <Form.Item name="old_price" label="Старая цена">
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          step={1}
                          addonAfter="₽"
                          placeholder="0"
                        />
                      </Form.Item>

                      <Form.Item
                        name="crm_article"
                        label="Артикул товара в CRM"
                        tooltip="Это артикул ТОВАРА (SKU) в CRM — не код филиала. Код филиала задаётся один раз для всей компании (или конкретного филиала) в настройках интеграции и применяется автоматически ко всем заказам этого филиала."
                      >
                        <Input placeholder="Артикул товара по умолчанию (можно переопределить по филиалам ниже)" />
                      </Form.Item>
                      <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 12 }}>
                        Код филиала в CRM настраивается один раз для компании —{' '}
                        <Link to="/settings/crm">перейти в настройки CRM</Link>.
                      </Text>

                      {/* Per-branch CRM-артикулы и стоп-лист */}
                      {allBranches.length > 0 && (
                        <div style={{ marginBottom: 24 }}>
                          <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            Настройки по филиалам
                          </Text>
                          <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
                            Артикул CRM и стоп-лист для каждого филиала
                          </Text>
                          {allBranches.map((branch) => (
                            <div key={branch.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                              <Text style={{ minWidth: 140 }}>{branch.name}</Text>
                              <Input
                                placeholder="Артикул CRM"
                                value={branchArticles[branch.id] || ''}
                                onChange={(e) =>
                                  setBranchArticles((prev) => ({
                                    ...prev,
                                    [branch.id]: e.target.value,
                                  }))
                                }
                                style={{ maxWidth: 250 }}
                              />
                              <Switch
                                checkedChildren="Стоп"
                                unCheckedChildren=""
                                checked={!!branchStopList[branch.id]}
                                onChange={(checked) =>
                                  setBranchStopList((prev) => ({
                                    ...prev,
                                    [branch.id]: checked,
                                  }))
                                }
                                style={branchStopList[branch.id] ? { backgroundColor: '#ff4d4f' } : {}}
                              />
                              {branchStopList[branch.id] && (
                                <Tag color="red" style={{ margin: 0 }}>На стопе</Tag>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      <Form.Item name="sort_order" label="Порядок сортировки">
                        <InputNumber style={{ width: '100%' }} placeholder="0" />
                      </Form.Item>

                      <Form.Item name="weight" label="Вес (текстом)" tooltip="Легаси-поле для обратной совместимости. Предпочтительно заполняйте структурированные поля ниже.">
                        <Input placeholder="Например: 300 г" />
                      </Form.Item>

                      {/* Feature #125: Структурированные поля порции — отображаются на карточке товара */}
                      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                        Порция (видно на карточке товара)
                      </Text>
                      <Row gutter={8}>
                        <Col span={8}>
                          <Form.Item name="pieces_count" label="Кол-во, шт" tooltip="Например 8 для ролла из 8 кусочков. Оставьте пустым, если не применимо.">
                            <InputNumber min={1} style={{ width: '100%' }} placeholder="8" />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item name="portion_value" label="Вес/объём">
                            <InputNumber min={0} step={0.1} style={{ width: '100%' }} placeholder="250" />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item name="portion_unit" label="Ед. изм.">
                            <Select
                              allowClear
                              placeholder="—"
                              options={[
                                { value: 'g', label: 'г' },
                                { value: 'kg', label: 'кг' },
                                { value: 'ml', label: 'мл' },
                                { value: 'l', label: 'л' },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      {/* КБЖУ — 4 поля в ряд */}
                      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                        КБЖУ
                      </Text>
                      <Row gutter={8}>
                        <Col span={6}>
                          <Form.Item name="calories" noStyle>
                            <InputNumber style={{ width: '100%' }} placeholder="Ккал" min={0} />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item name="proteins" noStyle>
                            <InputNumber style={{ width: '100%' }} placeholder="Б" min={0} />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item name="fats" noStyle>
                            <InputNumber style={{ width: '100%' }} placeholder="Ж" min={0} />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item name="carbs" noStyle>
                            <InputNumber style={{ width: '100%' }} placeholder="У" min={0} />
                          </Form.Item>
                        </Col>
                      </Row>

                      <div style={{ marginTop: 16 }}>
                        <Form.Item name="is_published" label="Опубликован" valuePropName="checked">
                          <Switch />
                        </Form.Item>

                        <Form.Item name="is_in_stop_list" label="Стоп-лист" valuePropName="checked">
                          <Switch />
                        </Form.Item>

                        <Form.Item
                          name="is_recommended"
                          label="Рекомендуемый товар"
                          valuePropName="checked"
                          tooltip="Товар появится в полосе «Рекомендуем» на главной странице сайта и мини-приложений"
                        >
                          <Switch />
                        </Form.Item>

                        <Form.Item name="stock_quantity" label="Остаток" tooltip="Оставьте пустым для неограниченного количества. При 0 товар отмечается как закончившийся.">
                          <InputNumber
                            min={0}
                            placeholder="Без ограничения"
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </TabPane>

              {/* ─── Вкладка 2: Изображения ────────────────────────────── */}
              <TabPane tab="Изображения" key="2">
                <Card bordered={false}>
                  {/* Главное изображение */}
                  <Title level={5}>Главное изображение</Title>
                  <ImageUpload
                    value={product?.image_url || form.getFieldValue('image_url')}
                    onChange={handleMainImageChange}
                    folder="products"
                    onUploadingChange={handleMainImageUploadingChange}
                    disabled={galleryUploading || saving}
                  />

                  {/* Галерея */}
                  <Title level={5} style={{ marginTop: 32 }}>
                    Галерея
                  </Title>
                  {product?.gallery && product.gallery.length > 0 ? (
                    <Image.PreviewGroup>
                    <Space wrap style={{ marginBottom: 16 }}>
                      {product.gallery.map((url, index) => (
                        <div key={`${index}-${url}`} style={{ position: 'relative', display: 'inline-block' }}>
                          <Image src={url} width={150} alt={`Галерея ${index + 1}`} />
                          <Popconfirm
                            title="Удалить изображение из галереи?"
                            icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                            okText="Удалить"
                            okButtonProps={{ danger: true }}
                            cancelText="Отмена"
                            onConfirm={() => handleGalleryRemove(index)}
                          >
                            <Button
                              danger
                              size="small"
                              shape="circle"
                              icon={<DeleteOutlined />}
                              aria-label={`Удалить фото ${index + 1} из галереи`}
                              disabled={mainImageUploading || galleryUploading || saving}
                              style={{ position: 'absolute', top: 4, right: 4 }}
                            />
                          </Popconfirm>
                        </div>
                      ))}
                    </Space>
                    </Image.PreviewGroup>
                  ) : (
                    <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                      Галерея пуста
                    </Text>
                  )}
                  <Upload
                    accept=".jpg,.jpeg,.png,.webp"
                    showUploadList={false}
                    beforeUpload={beforeGalleryUpload}
                    customRequest={handleGalleryUpload}
                    disabled={galleryUploading || mainImageUploading || saving}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      loading={galleryUploading}
                      disabled={galleryUploading || mainImageUploading || saving}
                    >
                      Добавить изображение в галерею
                    </Button>
                  </Upload>
                  {galleryUploading && (
                    <div style={{ marginTop: 8, maxWidth: 200 }}>
                      <Progress percent={galleryProgressKnown ? galleryProgress : 0} status="active" size="small" />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {galleryTransferComplete ? 'Обработка изображения…' : 'Загрузка...'}
                      </Text>
                    </div>
                  )}
                </Card>
              </TabPane>

              {/* ─── Вкладка 3: Стикеры и теги ─────────────────────────── */}
              <TabPane tab="Стикеры и теги" key="3">
                <Card bordered={false}>
                  <Form.Item name="sticker_ids" label="Стикеры">
                    <Select
                      mode="multiple"
                      placeholder="Выберите стикеры"
                      allowClear
                      optionFilterProp="label"
                      options={allStickers.map((s) => ({
                        value: s.id,
                        label: s.emoji ? `${s.emoji} ${s.name}` : s.name,
                      }))}
                    />
                  </Form.Item>

                  <Form.Item name="tag_ids" label="Теги">
                    <Select
                      mode="multiple"
                      placeholder="Выберите теги"
                      allowClear
                    />
                  </Form.Item>
                </Card>
              </TabPane>

              {/* ─── Вкладка 4: Варианты ───────────────────────────────── */}
              <TabPane tab="Варианты" key="4">
                <Card bordered={false}>
                  {product?.variants && product.variants.length > 0 ? (
                    <Table
                      dataSource={product?.variants}
                      columns={variantColumns}
                      rowKey="id"
                      pagination={false}
                      size="small"
                    />
                  ) : (
                    <Text type="secondary">Варианты не добавлены</Text>
                  )}
                </Card>
              </TabPane>

              {/* ─── Вкладка 5: Добавки ────────────────────────────────── */}
              <TabPane tab="Добавки" key="5">
                <Card bordered={false}>
                  <Form.Item
                    name="addon_category_ids"
                    label="Категории модификаторов"
                  >
                    <Select
                      mode="multiple"
                      placeholder="Выберите категории модификаторов"
                      allowClear
                      optionFilterProp="children"
                    >
                      {allAddonCategories.map((ac) => (
                        <Option key={ac.id} value={ac.id}>
                          {ac.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Card>
              </TabPane>

              {/* ─── Вкладка 6: Доступность (B2 dlvry-catalog) ────────────── */}
              {/* forceRender — иначе Form.Item "availability" не регистрируется в
                  сторе формы, пока пользователь не откроет вкладку, и save без
                  визита сюда отправит availability: null, стерев политику. */}
              <TabPane tab="Доступность" key="6" forceRender>
                <Card bordered={false}>
                  <Form.Item
                    name="availability"
                    rules={[
                      {
                        validator: (_rule, value) => {
                          const err = validateAvailabilityPolicy(value);
                          return err ? Promise.reject(new Error(err)) : Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <AvailabilityEditor />
                  </Form.Item>
                </Card>
              </TabPane>
            </Tabs>
          </Form>
        </Col>

        {/* ─── Боковая мета-информация ──────────────────────────────────── */}
        {!isNew && product && (
          <Col xs={24} lg={6}>
            <Card title="Информация" size="small">
              <Descriptions column={1} size="small" bordered={false}>
                <Descriptions.Item label="ID">{product.id}</Descriptions.Item>
                <Descriptions.Item label="Создан">{formatDate(product.created_at)}</Descriptions.Item>
                <Descriptions.Item label="Обновлён">{formatDate(product.updated_at)}</Descriptions.Item>
                <Descriptions.Item label="Статус">
                  {product.is_in_stop_list ? (
                    <Tag color="red">Стоп-лист</Tag>
                  ) : (product.stock_quantity !== null && product.stock_quantity !== undefined && product.stock_quantity <= 0) ? (
                    <Tag color="orange">Закончился</Tag>
                  ) : product.is_published ? (
                    <Tag color="green">Опубликован</Tag>
                  ) : (
                    <Tag color="default">Скрыт</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Остаток">
                  {product.stock_quantity !== null && product.stock_quantity !== undefined
                    ? product.stock_quantity
                    : 'Без ограничения'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        )}
      </Row>
    </div>
    </Spin>
  );
};

export default ProductDetailPage;


